import { Alert, Box, Button, Divider, Stack, Typography } from "@mui/material";
import AppDialog from "components/common/AppDialog";
import type { SettingsSection } from "features/integrationsBeta/readinessUi";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useEffect, useState } from "react";
import type { Schema } from "../jsonSchemaUtils";
import { diffConfigTree } from "./configDiff";
import ConfigDiffList, { ScalarChangeRow } from "./DiffView";
import { usePendingChanges, type PendingChange } from "./PendingChangesContext";
import { formatPendingValue } from "./pendingFormat";
import {
  useFlushPendingChanges,
  type FlushFailure,
} from "./useFlushPendingChanges";

const SECTION_ORDER: SettingsSection[] = [
  "status",
  "connections",
  "steps",
  "integration_config",
  "execution",
  "notifications",
  "webhooks",
  "schedules",
];

/** Changes of one entity: entity subheader + a readable row per change. */
function EntityChanges({ rows }: { rows: PendingChange[] }) {
  const entityLabel = rows[0].entityLabel;
  const named = entityLabel !== "Integration";
  return (
    <Box>
      {named && (
        <Typography variant="body2" fontWeight={700} sx={{ mb: 0.75 }}>
          {entityLabel}
        </Typography>
      )}
      <Stack spacing={0.75} sx={{ pl: named ? 1.5 : 0 }}>
        {rows.map((r) =>
          r.valueKind === "config" ? (
            <ConfigDiffList
              key={r.key}
              entries={diffConfigTree(
                r.oldValue,
                r.newValue,
                (r.schema ?? null) as Schema | null,
              )}
            />
          ) : (
            <ScalarChangeRow
              key={r.key}
              label={r.fieldLabel}
              oldText={formatPendingValue(r.valueKind, r.oldValue)}
              newText={formatPendingValue(r.valueKind, r.newValue)}
            />
          ),
        )}
      </Stack>
    </Box>
  );
}

/** Section rows grouped per entity, insertion-ordered. */
function groupByEntity(rows: PendingChange[]): PendingChange[][] {
  const groups = new Map<string, PendingChange[]>();
  for (const row of rows) {
    const key = `${row.entityType}:${row.entityId}`;
    const group = groups.get(key);
    if (group) group.push(row);
    else groups.set(key, [row]);
  }
  return Array.from(groups.values());
}

/**
 * Review of every staged change grouped by Settings section (UM
 * "Confirm Access Changes" style) with old -> new values; Confirm applies
 * the whole batch. Failed entities stay staged and are listed in an alert.
 */
export default function PendingReviewDialog({
  open,
  onClose,
  integrationId,
}: {
  open: boolean;
  onClose: () => void;
  integrationId: number;
}) {
  const dispatch = useAppDispatch();
  const { changes } = usePendingChanges();
  const flush = useFlushPendingChanges(integrationId);
  const [flushing, setFlushing] = useState(false);
  const [failures, setFailures] = useState<FlushFailure[]>([]);

  // Everything saved (or discarded elsewhere) -> nothing left to review.
  useEffect(() => {
    if (open && changes.length === 0 && !flushing) onClose();
  }, [open, changes.length, flushing]);

  const grouped = SECTION_ORDER.map((section) => ({
    section,
    rows: changes.filter((c) => c.section === section),
  })).filter((g) => g.rows.length > 0);

  const handleConfirm = async () => {
    setFlushing(true);
    setFailures([]);
    const total = changes.length;
    const fails = await flush(changes);
    setFlushing(false);
    if (fails.length === 0) {
      dispatch(
        pushNotification({
          severity: "success",
          message: `${total} change${total === 1 ? "" : "s"} saved.`,
        }),
      );
      onClose();
    } else {
      setFailures(fails);
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={flushing ? () => {} : onClose}
      title="Review & Save Changes"
      maxWidth="sm"
      showCloseIcon
      actions={
        <>
          <Button
            onClick={onClose}
            disabled={flushing}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={flushing}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {flushing ? "Saving..." : "Confirm & Save"}
          </Button>
        </>
      }
    >
      {failures.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Some changes could not be saved - they remain pending below.
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {failures.map((f, i) => (
              <li key={i}>
                <strong>{f.entityLabel}</strong>: {f.message}
              </li>
            ))}
          </Box>
        </Alert>
      )}
      {grouped.map(({ section, rows }, idx) => (
        <Box key={section}>
          {idx > 0 && <Divider sx={{ my: 2 }} />}
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={700}
            textTransform="uppercase"
            letterSpacing={0.6}
            display="block"
            sx={{ mb: 1 }}
          >
            {rows[0].sectionLabel}
          </Typography>
          <Stack spacing={1.5}>
            {groupByEntity(rows).map((entityRows) => (
              <EntityChanges
                key={`${entityRows[0].entityType}:${entityRows[0].entityId}`}
                rows={entityRows}
              />
            ))}
          </Stack>
        </Box>
      ))}
    </AppDialog>
  );
}
